import { Button, cn } from '@seen/ui'
import { IconDots } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { isEmpty } from 'lodash-es'
import { Fragment, type JSX, useMemo } from 'react'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { useAIProposalActions } from '#src/components/rich-text-editor/AIProposalActionsContext.tsx'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  type AIChoiceProposalSpec,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

export function ActionableChoice(props: NodeViewProps): JSX.Element {
  const optionsString = props.node.attrs.options as string | null
  const dropdownString = props.node.attrs.dropdown as string | null
  const selection = props.node.attrs.display as string | null

  const options = useMemo(
    () => optionsString?.split('|')?.map(extractTextFromHTML) ?? [],
    [optionsString]
  )

  const dropdownOptions = useMemo(() => {
    const dropdownList =
      dropdownString?.split('|')?.map(extractTextFromHTML) ?? null
    return (
      dropdownList?.map((option) => ({
        value: option,
        label: (
          <ComboboxOption isSelected={selection === option}>
            {option}
          </ComboboxOption>
        ),
        keywords: [option],
      })) ?? []
    )
  }, [dropdownString, selection])

  const selectionOptions = useMemo(
    () => [
      ...options.map((option) => ({
        value: option,
        label: (
          <ComboboxOption isSelected={selection === option}>
            {option}
          </ComboboxOption>
        ),
        keywords: [option],
      })),
      ...dropdownOptions,
      {
        value: '---',
        label: (
          <ComboboxOption isSelected={selection === '---'}>---</ComboboxOption>
        ),
        keywords: ['---'],
      },
    ],
    [options, dropdownOptions, selection]
  )

  const choiceProposal = useMemo<AIChoiceProposalSpec | null>(() => {
    for (const deco of props.decorations) {
      const cp = deco.spec?.aiChoiceProposal as AIChoiceProposalSpec | undefined
      if (cp) {
        return cp
      }
    }
    return null
  }, [props.decorations])

  const rawOptions = useMemo(
    () => optionsString?.split('|') ?? [],
    [optionsString]
  )

  const proposalActions = useAIProposalActions()

  /* Apply a value the clinician picked. With a pending choice suggestion for a
   * DIFFERENT value, this is reject-and-apply: one transaction sets the
   * selection AND drops the suggestion's decoration, so the still-pending
   * proposal can neither linger in the count nor re-render once the field is
   * selected; the explicit reject is then persisted through the same projection
   * the hover popover's Reject uses. Picking the suggested value itself goes
   * through the plain apply — the content-derived reconciler flips that record
   * to accepted. */
  const applyChoice = (option: string): void => {
    if (choiceProposal == null || option === choiceProposal.value) {
      props.updateAttributes({ display: option })
      return
    }
    // Capture the proposal BEFORE rejecting removes it, so Cmd+Z can restore
    // it (mirrors the hover popover's reject).
    const proposal = AI_PROPOSALS_PLUGIN_KEY.getState(
      props.editor.state
    )?.proposals.get(choiceProposal.id)
    props.editor
      .chain()
      .focus()
      .command(({ tr }) => {
        const pos = props.getPos()
        if (typeof pos !== 'number') {
          return false
        }
        tr.setNodeMarkup(pos, undefined, {
          ...props.node.attrs,
          display: option,
        })
        return true
      })
      .rejectAIProposal(choiceProposal.id)
      .run()
    if (proposal) {
      // Recorded AFTER the fused transaction (recording inside it would be
      // wiped by the undo extension's disarm-on-edit latch). `withDocUndo`
      // makes a single Cmd+Z restore the full pre-click state: the choice
      // application undone AND the suggestion re-pended. Optional-call: the
      // undo extension is absent on bare editors.
      props.editor.commands.recordRejectedAIProposals?.([proposal], {
        withDocUndo: true,
      })
    }
    proposalActions?.onReject([choiceProposal.id])
  }

  const editable = props.editor.isEditable

  return (
    <NodeViewWrapper
      className={cn(
        'inline-flex',
        !selection && 'px-px' // Without this, the blinking cursor gets covered by the background.
      )}
    >
      {!selection && (
        <>
          {options.map((option, idx) => {
            const isSuggested =
              choiceProposal != null && rawOptions[idx] === choiceProposal.value
            return (
              <Fragment key={option}>
                <Button
                  disabled={!editable}
                  variant="unstyled"
                  className={cn(
                    'h-auto px-1 py-0 text-center font-medium hover:brightness-90 focus-visible:z-10 focus-visible:rounded focus-visible:bg-background focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:brightness-100',
                    isSuggested
                      ? 'bg-green-100 text-green-900 ring-1 ring-green-500/40 ring-inset dark:bg-green-900/30 dark:text-green-200'
                      : 'bg-muted',
                    idx === 0 && 'rounded-l',
                    idx === options.length - 1 &&
                      isEmpty(dropdownOptions) &&
                      'rounded-r'
                  )}
                  onClick={() => {
                    if (isSuggested && choiceProposal) {
                      // Accepting the suggestion replaces the choice node with the
                      // proposed selection and clears the decoration.
                      props.editor
                        .chain()
                        .focus()
                        .acceptAIProposal(choiceProposal.id)
                        .run()
                    } else {
                      applyChoice(option)
                    }
                  }}
                  {...(isSuggested && {
                    'data-ai-proposal-id': choiceProposal.id,
                    ...(choiceProposal.label != null && {
                      'data-ai-proposal-label': choiceProposal.label,
                    }),
                  })}
                >
                  {extractTextFromHTML(option)}
                </Button>
                {idx !== options.length - 1 && (
                  <div className="grid w-px bg-muted">
                    <div
                      className={cn(
                        'my-1 w-full bg-foreground-tertiary',
                        !editable && 'opacity-50'
                      )}
                    />
                  </div>
                )}
              </Fragment>
            )
          })}
          {!isEmpty(dropdownOptions) && (
            <>
              <div className="grid w-px bg-muted">
                <div
                  className={cn(
                    'my-1 w-full bg-foreground-tertiary',
                    !editable && 'opacity-50'
                  )}
                />
              </div>
              <Combobox options={dropdownOptions} onSelect={applyChoice}>
                <Button
                  variant="unstyled"
                  size="unstyled"
                  disabled={!editable}
                  className="rounded-r bg-muted p-0 px-1 hover:brightness-90 focus-visible:z-10 focus-visible:my-0.5 focus-visible:rounded focus-visible:bg-background focus-visible:leading-6 focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  <IconDots size={16} />
                </Button>
              </Combobox>
            </>
          )}
        </>
      )}
      {!!selection && (
        <Combobox
          options={selectionOptions}
          onSelect={(value) => {
            if (value === '---') {
              props.updateAttributes({ display: null })
            } else {
              props.updateAttributes({ display: value })
            }
          }}
        >
          <Button
            variant="unstyled"
            size="unstyled"
            disabled={!editable}
            className="p-0 font-medium text-blue-600 focus-visible:z-10 focus-visible:my-0.5 focus-visible:rounded focus-visible:bg-background focus-visible:leading-6 focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-100"
          >
            {extractTextFromHTML(selection)}
          </Button>
        </Combobox>
      )}
    </NodeViewWrapper>
  )
}
